import { useState } from 'react'
import { scroller } from 'react-scroll'
import { NO_HUBSPOT_EMAIL_KEYWORD } from '@mc/enterprise-contact-form'
import { paths } from '@mc/paths'
import { EventTypes, track } from '@mc/track-event'
import { setLeadEmail } from './localStorage'

const portalId = '21051809'
const formId = '56889ba8-fc5c-4767-9825-6d5a461b777e'
const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`

interface EmailCaptureFields {
  email: string
  gdprOptIn: boolean
}

const getHubspotCookie = () =>
  document.cookie.replace(
    /(?:(?:^|.*;\s*)hubspotutk\s*=\s*([^;]*).*$)|^.*$/,
    '$1',
  )

const trackHeroEmailCaptured = (email: string) => {
  track(EventTypes.MAW_LEAD_SUBMITTED, {
    email_address: email,
    form_type: 'progressive',
    page_path: paths.enterpriseHomepage,
  })
}

const postToHubspot = ({ email, gdprOptIn }: EmailCaptureFields) => {
  const body = {
    submittedAt: Date.now().toString(),
    fields: [{ name: 'email', value: email }],
    context: {
      hutk: getHubspotCookie(),
      pageUri: 'www.masterclass.com/for-business',
      pageName: 'MasterClass - For Business',
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: gdprOptIn,
        text: 'Consent to process',
        communications: [
          {
            subscriptionTypeId: 26837169,
            text: 'By sharing your email, you agree to our Terms of Service and Privacy Policy',
            value: gdprOptIn,
          },
        ],
      },
    },
  }

  return fetch(hubspotUrl, {
    method: 'POST',
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
}

const checkResponseError = (res: { ok: unknown }) => {
  if (res.ok) {
    return
  }

  throw new Error()
}

export const useEmailCapture = () => {
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const goToContactForm = () =>
    scroller.scrollTo('contact-form', {
      duration: 700,
      smooth: true,
      offset: -70,
    })

  const clearError = () => {
    if (hasError) setHasError(false)
  }

  const handleSubmit = ({ email, gdprOptIn }: EmailCaptureFields) =>
    email.includes(NO_HUBSPOT_EMAIL_KEYWORD)
      ? Promise.resolve({ ok: true })
      : postToHubspot({ email, gdprOptIn })

  const onSubmitEmail = (email: string, gdprOptIn: boolean) => {
    setLoading(true)

    handleSubmit({ email, gdprOptIn })
      .then((res) => {
        checkResponseError(res)
        trackHeroEmailCaptured(email)
        setLeadEmail(email)
        goToContactForm()
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return {
    loading,
    hasError,
    clearError,
    onSubmitEmail,
  }
}
